import ButtonClick from 'Components/ButtonClick';
import { CheckBoxItem } from 'Components/CheckBox';
import React, { useEffect } from 'react';
import { VIETNAM_COUNTRY_CODE } from 'Utilities/constants';
import FieldLabel from 'Components/FieldLabel';
import InputText from 'Components/InputText';
import { useLoadingBar, useToast } from 'Components/LayoutUtilities';
import RadioButton from 'Components/RadioButton';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import UserServiceV1 from 'Services/v1/user';
import { DTOUserProfile } from 'Services/v1/user/dto';
import {
  getLocations,
  getSelectedLocation,
  locationSelectors
} from 'Stores/locations';
import './index.scss';

interface BusinessProfileProps {
  data: DTOUserProfile;
}

const BusinessProfile = (props: BusinessProfileProps) => {
  const { t } = useTranslation();

  const { startLoadingBar, stopLoadingBar, loadingBarState } = useLoadingBar();
  const toast = useToast();
  const profile = props.data;

  const {
    control,
    watch,
    formState: { errors, dirtyFields },
    setValue,
    getValues,
    resetField,
    handleSubmit
  } = useForm();

  const dispatch = useDispatch();

  const watchProvince = watch('province');
  const watchDistrict = watch('district');
  const watchVillage = watch('village');

  const cityData = useSelector(locationSelectors.get).city.data;
  const districtData = useSelector(locationSelectors.get).district.data;
  const wardData = useSelector(locationSelectors.get).ward.data;

  const titleOptions: CheckBoxItem[] = [
    { label: t('global_title_male'), value: '1' },
    { label: t('global_title_female'), value: '0' }
  ];

  // Load city list on mount
  useEffect(() => {
    dispatch(getLocations({ level: 'city', id: VIETNAM_COUNTRY_CODE }));
  }, []);

  // Load district when province changes
  useEffect(() => {
    if (watchProvince) {
      dispatch(getLocations({ level: 'district', id: watchProvince }));
    }
  }, [watchProvince]);

  // Load ward when district changes
  useEffect(() => {
    if (watchDistrict) {
      dispatch(getLocations({ level: 'ward', id: watchDistrict }));
    }
  }, [watchDistrict]);

  // Get selected ward info
  useEffect(() => {
    if (watchVillage) {
      dispatch(getSelectedLocation({ level: 'ward', id: watchVillage }));
    }
  }, [watchVillage]);

  // Set form values from profile
  useEffect(() => {
    if (!profile) return;
    setValue('customerCode', profile.customerCode);
    setValue('companyName', profile.companyName);
    setValue('taxNumber', profile.taxNumber);
    setValue('companyPhone', profile.companyPhone);
    setValue('gender', profile.gender);
    setValue('companyRepresentative', profile.companyRepresentative);
    setValue('email', profile.email);
    // Set hidden fields to trigger location chain load
    setValue('province', profile.province);
    setValue('district', profile.district);
    setValue('village', profile.village);
    setValue('address', profile.address);
    setValue('address_full', profile.addressFull);
  }, [profile]);

  // Build address_full after location data loaded
  useEffect(() => {
    if (!profile) return;

    const provinceName = cityData?.find(p => String(p.value) === String(profile.province))?.label || '';
    const districtName = districtData?.find(d => String(d.value) === String(profile.district))?.label || '';
    const villageName = wardData?.find(w => String(w.value) === String(profile.village))?.label || '';

    const addressFull = profile?.addressFull ||
      [ profile?.address , villageName, districtName, provinceName]
        .filter(Boolean)
        .join(', ');

    setValue('address_full', addressFull);
  }, [profile, cityData, districtData, wardData]);

  const onSubmit = async (values: any) => {
    const payload = { ...values };
    startLoadingBar();
    const resp = await UserServiceV1.updateProfile(payload);
    const { error } = resp;
    if (!error) {
      toast('success', t('message_update_profile_successfully'));
    } else {
      toast('error', t('message_update_profile_failed'));
    }
    stopLoadingBar();
  };

  return (
    <form className={'business-profile-form'} onSubmit={handleSubmit(onSubmit)}>
      <div className="row gap-5">
        <div className="col-md-12">
          <FieldLabel className={'mb-2'} value={t('label_customer_code')} />
          <Controller
            control={control}
            render={({ field }) => (
              <InputText {...field} disabled={true} placeholder={t('label_customer_code')} />
            )}
            name={'customerCode'}
          />
        </div>
        <div className="col-md-12">
          <FieldLabel className={'mb-2'} value={t('label_company_name')} />
          <Controller
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder={t('label_company_name')} />
            )}
            name={'companyName'}
          />
        </div>
        <div className="col-md-12">
          <FieldLabel className={'mb-2'} value={t('label_email')} />
          <Controller
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder={t('label_email')} />
            )}
            name={'email'}
          />
        </div>
        <div className="col-md-12">
          <FieldLabel className={'mb-2'} value={t('label_tax_number')} />
          <Controller
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder={t('label_tax_number')} />
            )}
            name={'taxNumber'}
          />
        </div>
        <div className="col-md-12">
          <FieldLabel className={'mb-2'} value={t('label_company_phone')} />
          <Controller
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder={t('label_company_phone')} />
            )}
            name={'companyPhone'}
          />
        </div>
        <div className="col-md-12">
          <FieldLabel className={'mb-2'} value={t('label_company_representative')} />
          <Controller
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder={t('label_company_representative')} />
            )}
            name={'companyRepresentative'}
          />
        </div>
        <div className="col-md-12">
          <FieldLabel className={'mb-2'} value={t('label_title')} />
          <Controller
            control={control}
            name={'gender'}
            render={({ field }) => (
              <RadioButton
                {...field}
                options={titleOptions}
                radioGroupClassName="w-100 d-flex"
              />
            )}
          />
        </div>

        {/* Address full - hidden location fields để trigger load chain */}
        <div style={{ display: 'none' }}>
          <Controller control={control} render={({ field }) => <input {...field} />} name={'province'} />
          <Controller control={control} render={({ field }) => <input {...field} />} name={'district'} />
          <Controller control={control} render={({ field }) => <input {...field} />} name={'village'} />
          <Controller control={control} render={({ field }) => <input {...field} />} name={'address'} />
        </div>

        <div className="col-md-12">
          <FieldLabel className={'mb-2'} value={t('label_address')} />
          <Controller
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder={t('label_address')} />
            )}
            name={'address_full'}
          />
        </div>

        <ButtonClick
          variant="primary"
          htmlType="submit"
          containerClassName={'text-right'}
          disabled={loadingBarState === 'loading'}
          className={'business-profile-form-save-button'}
        >
          {t('global_save')}
        </ButtonClick>
      </div>
    </form>
  );
};

export default BusinessProfile;